import { Box, Grid, TextField, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineInstruction from "../components/GuidelineInstruction";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import addNewIdentityScreenshot from "./images/add_new_identity_info.png";
import assignRolesScreenshot from "./images/assign_roels_all_apps.png";
import setupClientCredentialsScreenshot from "./images/client_credentials.png";
import navigateToIdentityAccessScreenshot from "./images/go_to_settings.png";

const PaloAltoPrismaAccessBrowserIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const [loading, setLoading] = useState(false);
  const [clientID, setClientID] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated(
    "paloalto_prisma_access_browser"
  );
  const queryClient = useQueryClient();

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerPaloAltoPrismaAccessBrowserIntegration } = useAPI();

  useEffect(() => {
    setLoading(isIntegratedLoading);
  }, [isIntegratedLoading]);

  const onSubmit = async () => {
    setLoading(true);
    try {
      await registerPaloAltoPrismaAccessBrowserIntegration(
        clientID,
        clientSecret
      );
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      Toast.success("Palo Alto Prisma Access Browser integration successful");
    } catch (error) {
      if (error.response) {
        Toast.error(`Something went wrong: ${error.response.data.detail}`);
      } else {
        Toast.error("An error occurred while processing the request.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated && !loading
            ? "Once integrated, Breeze will scan your Palo Alto Prisma Access Browser for security gaps."
            : ""
        }
        image={logos.PrismaAccessBrowserLogo}
        name={"Palo Alto Prisma Access Browser"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>
                Palo Alto Prisma Access Browser is integrated!
              </Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Navigate to Identity & Access">
                  <GuidelineInstruction text="Open Strata Cloud Manager console and go to Settings" />
                  <GuidelineInstruction text="Click Identity & Access from the left navigation panel." />
                  <GuidelineInstruction text="Click the Add Identity button in the top right." />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: navigateToIdentityAccessScreenshot,
                        altext: "Navigate to Identity & Access",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Add a New Identity">
                  <GuidelineInstruction text="Select Identity Type as Service Account." />
                  <GuidelineInstruction text="Enter a Service Account Name for example, 'BreezePlatform'." />
                  <GuidelineInstruction text="Click Next to proceed." />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: addNewIdentityScreenshot,
                        altext: "Add a New Identity",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Set Up Client Credentials">
                  <ScreenshotAccordion
                    images={[
                      {
                        file: setupClientCredentialsScreenshot,
                        altext: "Set Up Client Credentials",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <GuidelineInstruction text="The system will generate Client ID and Client Secret." />
                  <GuidelineInstruction text="Copy the Client ID and Client Secret to the fields below." />
                  <Grid sx={{ width: "700px" }} spacing={1}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        variant="filled"
                        name="clientID"
                        label="Client ID"
                        value={clientID}
                        onChange={(e) => setClientID(e.target.value)}
                        autoComplete="off"
                        disabled={guideMode}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ mt: "10px" }}>
                      <TextField
                        fullWidth
                        variant="filled"
                        name="clientSecret"
                        label="Client Secret"
                        value={clientSecret}
                        onChange={(e) => setClientSecret(e.target.value)}
                        autoComplete="off"
                        disabled={guideMode}
                      />
                    </Grid>
                  </Grid>
                  <GuidelineInstruction text="Click Next to continue." />
                </GuidelineStep>
                <GuidelineStep title="Assign Roles">
                  <Box>
                    <GuidelineInstruction text="You can assign roles based on specific apps or services." />
                    <br />
                    <Typography variant="h6">Assign Roles</Typography>
                    <GuidelineInstruction text="1. Under Assign Roles, choose one of the following options:" />
                    <Box sx={{ pl: "20px" }}>
                      <GuidelineInstruction text="a. Select 'All Apps & Services' - Adding all apps and services will allow to reuse the credentials for other Prisma services." />
                      <GuidelineInstruction text="b. Select a specific application: Prisma Access Browser." />
                    </Box>
                    <GuidelineInstruction text="2. Choose the Role 'View Only Administrator'." />
                    <GuidelineInstruction text="3. Click Submit." />
                  </Box>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: assignRolesScreenshot,
                        altext: "Assign Roles",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Connect Breeze">
                  <PrimaryButton
                    onClick={onSubmit}
                    disabled={loading || !clientID || !clientSecret}
                  >
                    Connect
                  </PrimaryButton>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default PaloAltoPrismaAccessBrowserIntegration;
